import React from 'react';
import { noop } from 'lodash';
import PropTypes from 'prop-types';
import { DesktopIcon, MobileSimpleIcon, TabletIcon, TargetIcon } from 'taboola-ultimate-ui';
import { CollapsibleFormSection } from 'components';
import CommonCollapsibleCard from 'components/CommonCollapsibleCard/CommonCollapsibleCard';
import { FORM_MODES } from 'config/formModes';
import { isProgrammaticAccount } from 'modules/account-management/accountType';
import SectionHeader from 'modules/campaigns/components/Form/SectionHeader/SectionHeader';
import { ENTITY_MESSAGE_PREFIX } from 'modules/campaigns/config/routes';
import {
    alertKeys,
    checkIsCompatiblePlatforms,
} from 'modules/campaigns/config/validations/campaign/platformValidations';
import BrandSafety from 'modules/campaigns/modules/common-campaign-form/components/BrandSafety';
import BrowserTargeting from 'modules/campaigns/modules/common-campaign-form/components/BrowserTargeting';
import GeoTargeting from 'modules/campaigns/modules/common-campaign-form/components/GeoTargeting/GeoTargeting';
import { HighImpactTargeting } from 'modules/campaigns/modules/common-campaign-form/components/HighImpactTargeting/HighImpactTargeting';
import { InventoryTypeTargeting } from 'modules/campaigns/modules/common-campaign-form/components/InventoryTypeTargeting/InventoryTypeTargeting';
import MissingTargetingFeatures from 'modules/campaigns/modules/common-campaign-form/components/MissingTargetingFeatures';
import OsTargetingField from 'modules/campaigns/modules/common-campaign-form/components/OsTargeting';
import PlatformTargeting from 'modules/campaigns/modules/common-campaign-form/components/PlatformTargeting/PlatformTargeting';
import PremiumSite from 'modules/campaigns/modules/common-campaign-form/components/PremiumSite';
import { SiteTargetingField } from 'modules/campaigns/modules/common-campaign-form/components/SiteTargeting/SiteTargetingField';
import { TaboolaSelectCheckbox } from 'modules/campaigns/modules/common-campaign-form/components/SupplyTargeting/TaboolaSelectCheckbox';
import { SupplyTargetingSelectField } from 'modules/campaigns/modules/common-campaign-form/components/SupplyTargeting/components/SupplyTargetingSelectField/SupplyTargetingSelectField';
import { SupplyTargetingSlider } from 'modules/campaigns/modules/common-campaign-form/components/SupplyTargeting/components/SupplyTargetingSlider/SupplyTargetingSlider';
import { SupplyTargetingField } from 'modules/campaigns/modules/common-campaign-form/components/SupplyTargeting/components/SupplyTargettingDDField/SupplyTargetingField';
import { SUPPLY_TARGETING_TYPES } from 'modules/campaigns/modules/common-campaign-form/components/SupplyTargeting/const';
import { useIsSupplyTargetingPermitted } from 'modules/campaigns/modules/common-campaign-form/components/SupplyTargeting/hooks/useIsSupplyTargetingPermitted';
import ThirdPartyBrandSafety from 'modules/campaigns/modules/common-campaign-form/components/ThirdPartyBrandSafety';
import WifiTargeting from 'modules/campaigns/modules/common-campaign-form/components/WifiTargeting/WifiTargeting';
import { MARKETING_OBJECTIVE_KEYS, PLATFORMS } from 'modules/campaigns/modules/common-campaign-form/config';
import { FEATURE_FLAGS, useConfigMatch } from 'modules/taboola-common-frontend-modules/account-configurations';
import { useFormDataContext } from 'modules/taboola-common-frontend-modules/formData';
import { FormattedMessage } from 'modules/taboola-common-frontend-modules/i18n';
import { useAdvancedOptionsSection } from './hooks/useAdvancedOptionsSection';
import styles from './targetingSection.module.scss';

const getCheckboxColor = ({ id, platformTargeting }) => {
    if (checkIsCompatiblePlatforms(platformTargeting) || !alertKeys.includes(id)) return;
    return styles.alertColor;
};

const shouldEnableItem = conditionData => {
    return conditionData?.marketingObjective !== MARKETING_OBJECTIVE_KEYS.MOBILE_APP_INSTALL;
};
const platformsWithCondition = [
    {
        id: PLATFORMS.DESKTOP,
        icon: DesktopIcon,
        enableCondition: shouldEnableItem,
    },
    {
        id: PLATFORMS.MOBILE,
        icon: MobileSimpleIcon,
    },
    {
        id: PLATFORMS.TABLET,
        icon: TabletIcon,
        enableCondition: shouldEnableItem,
    },
];

const TargetingSection = ({
    isIncludeHighImpactTargeting,
    isIncludeSupplyTargeting = true,
    messageIdPrefix = ENTITY_MESSAGE_PREFIX.CAMPAIGN,
    shouldInitiallyCollapse,
}) => {
    const { isSupplyPermitted, isTaboolaSelectEnabled, isSupplySliderPermitted } =
        useIsSupplyTargetingPermitted() || {};
    const { isInitiallyCollapsed, advancedOptionsSubtitle } = useAdvancedOptionsSection(isIncludeHighImpactTargeting);
    const { mode, data, formAccount } = useFormDataContext();
    const campaignTargeting = data?.campaignTargeting;
    const isEditOrDuplicateForm = mode === FORM_MODES.EDIT || mode === FORM_MODES.DUPLICATE;
    const isSupplyTargetingSelectField = isIncludeSupplyTargeting && !isSupplyPermitted && isTaboolaSelectEnabled;
    const isOptimalScaleEnvironment = data?.campaignTargeting?.supplyTargeting === SUPPLY_TARGETING_TYPES.ALL;

    const isInventoryTypeTargetingEnabled = useConfigMatch({
        [FEATURE_FLAGS.INVENTORY_TYPE_TARGETING_ENABLED]: 'true',
    });

    return (
        <CommonCollapsibleCard
            id="CAMPAIGN_TARGETING"
            header={
                <SectionHeader
                    headerIcon={<TargetIcon />}
                    headerText={<FormattedMessage id={`${messageIdPrefix}.editor.targeting`} />}
                />
            }
            isInitiallyCollapsed={shouldInitiallyCollapse}
        >
            {isEditOrDuplicateForm && campaignTargeting && (
                <MissingTargetingFeatures className={styles['missing-features']} />
            )}
            <GeoTargeting />
            <PlatformTargeting
                platforms={platformsWithCondition}
                getCheckboxColor={isProgrammaticAccount(formAccount) ? noop : getCheckboxColor}
            />
            {isInventoryTypeTargetingEnabled && <InventoryTypeTargeting />}
            <WifiTargeting />
            <OsTargetingField />
            <BrowserTargeting />
            {isIncludeSupplyTargeting && isSupplyPermitted && isSupplySliderPermitted ? (
                <SupplyTargetingSlider />
            ) : null}
            <CollapsibleFormSection
                title={<FormattedMessage id="campaign.advancedOptions" defaultMessage="Advanced Options" />}
                subtitle={advancedOptionsSubtitle}
                isInitiallyCollapsed={isInitiallyCollapsed}
                sectionId="CAMPAIGN_SUBSECTION_ADVANCED_TARGETING"
                testId="targeting-advanced-options"
            >
                {isSupplyTargetingSelectField ? <SupplyTargetingSelectField /> : null}
                {isIncludeSupplyTargeting && isSupplyPermitted && !isSupplySliderPermitted ? (
                    <SupplyTargetingField />
                ) : null}
                <SiteTargetingField />
                <ThirdPartyBrandSafety />
                {isSupplyTargetingSelectField && isOptimalScaleEnvironment ? <TaboolaSelectCheckbox /> : null}
                {isIncludeHighImpactTargeting ? <HighImpactTargeting /> : null}
                <PremiumSite />
                <BrandSafety />
            </CollapsibleFormSection>
        </CommonCollapsibleCard>
    );
};

TargetingSection.propTypes = { messageIdPrefix: PropTypes.string };

TargetingSection.defaultProps = {
    messageIdPrefix: ENTITY_MESSAGE_PREFIX.CAMPAIGN,
};

export default TargetingSection;
